import React, { useState, useCallback, useMemo, useRef } from "react";
import useHistory from "routing/useHistory";
import AppConfig from "util/AppConfig";
import { MenuItem, ButtonGroup } from "components/bootstrap";
import { ShareButton } from "components/common";
import ExportModal from "views/components/export/ExportModal";
import DebugOverlay from "views/components/DebugOverlay";
import onSaveNewDashboard from "views/logic/views/OnSaveNewDashboard";
import * as ViewPermissions from "views/Permissions";
import useSearchPageLayout from "hooks/useSearchPageLayout";
import View from "views/logic/views/View";
import usePermissions from "hooks/usePermissions";
import useGetPermissionsByScope from "hooks/useScopePermissions";
import useProductName from "brand-customization/useProductName";
import EntityShareModal from "components/permissions/EntityShareModal";
import { executePluggableDashboardDuplicationHandler as executePluggableDuplicationHandler } from "views/logic/views/pluggableSaveViewFormHandler";
import useSaveViewFormControls from "views/hooks/useSaveViewFormControls";
import useView from "views/hooks/useView";
import useIsNew from "views/hooks/useIsNew";
import useViewsDispatch from "views/stores/useViewsDispatch";
import usePluginEntities from "hooks/usePluginEntities";
import { updateView } from "views/logic/slices/viewSlice";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import SaveDashboardButton from "views/components/searchbar/SaveDashboardButton";
import SaveAsDashboardButton from "views/components/searchbar/SaveAsDashboardButton";
import { MoreActionsMenu } from "components/common/MoreActions";
import DashboardPropertiesModal from "./dashboard/DashboardPropertiesModal";
import BigDisplayModeConfiguration from "./dashboard/BigDisplayModeConfiguration";
const _isAllowedToEdit = (view, isPermitted) => isPermitted([ViewPermissions.View.Edit(view.id)]) || view.type === View.Type.Dashboard && isPermitted([`dashboards:edit:${view.id}`]);
const immutableDashboardInfo = (productName) => `This dashboard is read-only because it is managed by ${productName}. Use "Save as" to create an editable copy.`;
const DashboardActionsMenu = () => {
  const view = useView();
  const isNewView = useIsNew();
  const { isPermitted } = usePermissions();
  const {
    viewActions: {
      save: { isShown: showSaveButton },
      saveAs: { isShown: showSaveNewButton },
      share: { isShown: showShareButton },
      actionsDropdown: { isShown: showDropDownButton }
    }
  } = useSearchPageLayout();
  const pluggableSaveViewControls = useSaveViewFormControls();
  const [shareDashboardOpen, setShareViewOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [saveNewDashboardOpen, setSaveNewDashboardOpen] = useState(false);
  const [editDashboardOpen, setEditDashboardOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const sendTelemetry = useSendTelemetry("dashboard");
  const { loadingScopePermissions, scopePermissions } = useGetPermissionsByScope({ _scope: view.scope });
  const allowedToEdit = _isAllowedToEdit(view, isPermitted) && !!(scopePermissions == null ? void 0 : scopePermissions.is_mutable);
  const isImmutable = !isNewView && !loadingScopePermissions && !(scopePermissions == null ? void 0 : scopePermissions.is_mutable);
  const productName = useProductName();
  const debugOverlay = AppConfig.gl2DevMode() && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(MenuItem, { divider: true }), /* @__PURE__ */ React.createElement(MenuItem, { onSelect: () => setDebugOpen(true), icon: "code" }, "Debug"));
  const dispatch = useViewsDispatch();
  const history = useHistory();
  const pluggableDashboardActions = usePluginEntities("views.components.dashboardActions");
  const modalRefs = useRef({});
  const dashboardActions = useMemo(
    () => pluggableDashboardActions.map(({ component: PluggableDashboardAction, key }) => /* @__PURE__ */ React.createElement(
      PluggableDashboardAction,
      {
        key: `dashboard-action-${key}`,
        dashboard: view,
        modalRef: () => modalRefs.current[key]
      }
    )),
    [pluggableDashboardActions, view]
  );
  const dashboardActionModals = useMemo(
    () => pluggableDashboardActions.filter(({ modal }) => !!modal).map(({ modal: ActionModal, key }) => /* @__PURE__ */ React.createElement(
      ActionModal,
      {
        key: `dashboard-action-modal-${key}`,
        dashboard: view,
        ref: (r) => {
          modalRefs.current[key] = r;
        }
      }
    )),
    [pluggableDashboardActions, view]
  );
  const _onSaveNewDashboard = useCallback(
    async (newDashboard, entityShare) => {
      if (!isNewView && view.scope === "ILLUMINATE") {
        sendTelemetry(TELEMETRY_EVENT_TYPE.DASHBOARD_ACTION.ILLUMINATE_DASHBOARD_CLONED, {
          app_pathname: "dashboard",
          app_action_value: "illuminate-dashboard-clone",
          event_details: { dashboard_title: view.title }
        });
      } else {
        sendTelemetry(TELEMETRY_EVENT_TYPE.DASHBOARD_ACTION.DASHBOARD_NEW_SAVED, {
          app_pathname: "dashboard",
          app_action_value: "dashboard-save-new"
        });
      }
      if (!isNewView) {
        const dashboardWithPluginData = await executePluggableDuplicationHandler(
          newDashboard,
          isPermitted,
          pluggableSaveViewControls
        );
        return dispatch(onSaveNewDashboard(dashboardWithPluginData, history, entityShare, view.id));
      }
      return dispatch(onSaveNewDashboard(newDashboard, history, entityShare));
    },
    [
      isPermitted,
      dispatch,
      history,
      pluggableSaveViewControls,
      sendTelemetry,
      view.id,
      view.scope,
      view.title,
      isNewView
    ]
  );
  const _onUpdateView = useCallback(
    (updatedView) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.DASHBOARD_ACTION.DASHBOARD_UPDATED, {
        app_pathname: "dashboard",
        app_action_value: "dashboard-update"
      });
      return dispatch(updateView(updatedView));
    },
    [dispatch, sendTelemetry]
  );
  return /* @__PURE__ */ React.createElement(ButtonGroup, null, showSaveButton && /* @__PURE__ */ React.createElement(
    SaveDashboardButton,
    {
      userIsAllowedToEdit: allowedToEdit,
      openSaveAsModal: () => setSaveNewDashboardOpen(true),
      disabledInfo: isImmutable ? immutableDashboardInfo(productName) : void 0
    }
  ), showSaveNewButton && /* @__PURE__ */ React.createElement(
    SaveAsDashboardButton,
    {
      onClick: () => setSaveNewDashboardOpen(true),
      openSaveAsModal: () => setSaveNewDashboardOpen(true)
    }
  ), showShareButton && /* @__PURE__ */ React.createElement(
    ShareButton,
    {
      entityType: "dashboard",
      entityId: view.id,
      onClick: () => setShareViewOpen(true),
      bsSize: "md",
      disabledInfo: isNewView && "Only saved dashboards can be shared."
    }
  ), showDropDownButton && /* @__PURE__ */ React.createElement(MoreActionsMenu, { id: "query-tab-actions-dropdown", pullRight: true, keepMounted: true, solid: true }, dashboardActions.length > 0 && /* @__PURE__ */ React.createElement(React.Fragment, null, dashboardActions, /* @__PURE__ */ React.createElement(MenuItem, { divider: true })), /* @__PURE__ */ React.createElement(MenuItem, { onSelect: () => setEditDashboardOpen(true), disabled: isNewView || !allowedToEdit, icon: "edit" }, "Edit metadata"), /* @__PURE__ */ React.createElement(MenuItem, { onSelect: () => setExportOpen(true), icon: "download" }, "Export"), debugOverlay, /* @__PURE__ */ React.createElement(MenuItem, { divider: true }), /* @__PURE__ */ React.createElement(BigDisplayModeConfiguration, { view, disabled: isNewView })), debugOpen && /* @__PURE__ */ React.createElement(DebugOverlay, { show: true, onClose: () => setDebugOpen(false) }), saveNewDashboardOpen && /* @__PURE__ */ React.createElement(
    DashboardPropertiesModal,
    {
      show: true,
      view: view.toBuilder().newId().build(),
      dashboardId: view.id,
      title: "Save new dashboard",
      submitButtonText: "Create dashboard",
      onClose: () => setSaveNewDashboardOpen(false),
      onSave: (newDashboard, entityShare) => _onSaveNewDashboard(newDashboard, entityShare)
    }
  ), editDashboardOpen && /* @__PURE__ */ React.createElement(
    DashboardPropertiesModal,
    {
      show: true,
      view,
      dashboardId: view.id,
      title: "Editing dashboard",
      submitButtonText: "Update dashboard",
      onClose: () => setEditDashboardOpen(false),
      onSave: _onUpdateView
    }
  ), shareDashboardOpen && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: view.id,
      entityType: "dashboard",
      entityTitle: view.title,
      description: "Search for a User or Team to add as collaborator on this dashboard.",
      onClose: () => setShareViewOpen(false)
    }
  ), exportOpen && /* @__PURE__ */ React.createElement(ExportModal, { view, closeModal: () => setExportOpen(false) }), dashboardActionModals);
};
export default DashboardActionsMenu;
